import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getS3Url } from '@/config/s3.config';
import { PromoBannerResponse } from '@repo/types/src/promo-banner';
import { Eye } from 'lucide-react';

const PHONE_WIDTH = 300;
const PHONE_HEIGHT = Math.round(PHONE_WIDTH * 2.167);
const FRAME = 10;

interface PhonePreviewProps {
  banner: PromoBannerResponse | undefined;
  label: string;
}

const PhonePreview = ({ banner, label }: PhonePreviewProps) => (
  <div className="flex flex-col items-center gap-3">
    <p className="text-sm font-semibold text-zinc-600">{label}</p>

    <div
      style={{
        width: PHONE_WIDTH,
        height: PHONE_HEIGHT,
        borderRadius: 52,
        backgroundColor: '#111',
        padding: FRAME,
        boxShadow:
          '0 0 0 1px #2a2a2a, 0 28px 80px rgba(0,0,0,0.5), inset 0 0 0 1px #3a3a3a',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: 1,
          borderRadius: 42,
          overflow: 'hidden',
          backgroundColor: '#000000',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            backgroundColor: '#FEFBFB',
            paddingTop: 12,
            paddingBottom: 8,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 88,
              height: 24,
              backgroundColor: '#000',
              borderRadius: 14,
            }}
          />
        </div>

        <div
          style={{
            backgroundColor: '#FEFBFB',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid #F1DAE0',
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, color: '#6A314A' }}>
            Shop
          </span>
        </div>

        {banner ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              padding: 11,
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#F8EBEE',
              gap: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 9,
                maxWidth: '52%',
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  margin: 0,
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: '#000000',
                }}
              >
                {banner.textParts.map((part, i) =>
                  part.highlight ? (
                    <strong
                      key={i}
                      style={{ color: '#913F5E', fontWeight: 700 }}
                    >
                      {part.text}
                      {i < banner.textParts.length - 1 ? ' ' : ''}
                    </strong>
                  ) : (
                    <span key={i} style={{ fontWeight: 400 }}>
                      {part.text}
                      {i < banner.textParts.length - 1 ? ' ' : ''}
                    </span>
                  ),
                )}
              </p>
              <a
                href={banner.buttonLink}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'block',
                  backgroundColor: '#D38A9E',
                  border: '1px solid #D38A9E',
                  borderRadius: 30,
                  padding: '6px 9px',
                  textAlign: 'center',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 700,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                {banner.buttonText}
              </a>
            </div>
            <img
              src={getS3Url(banner.image)}
              alt="Promo"
              style={{
                width: 94,
                height: 79,
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#F8EBEE',
              padding: '22px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 11, color: '#AE5071' }}>
              Aucune bannière active
            </span>
          </div>
        )}

        <div
          style={{
            flex: 1,
            backgroundColor: '#FEFBFB',
            padding: '12px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div
            style={{
              height: 7,
              backgroundColor: '#F1DAE0',
              borderRadius: 4,
              width: '52%',
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <div
              style={{
                height: 28,
                backgroundColor: '#E6BBC6',
                borderRadius: 20,
                flex: 1,
              }}
            />
            <div
              style={{
                height: 28,
                backgroundColor: '#F1DAE0',
                borderRadius: 20,
                flex: 1,
              }}
            />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              style={{
                height: 42,
                backgroundColor: '#F8EBEE',
                borderRadius: 10,
                opacity: Math.max(0.15, 1 - (i - 1) * 0.18),
              }}
            />
          ))}
        </div>

        <div
          style={{
            backgroundColor: '#FEFBFB',
            paddingBottom: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 100,
              height: 5,
              backgroundColor: '#1a1a1a',
              borderRadius: 3,
              opacity: 0.2,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

interface Props {
  banners: PromoBannerResponse[];
}

export const PromoBannerPreviewModal = ({ banners }: Props) => {
  const frBanner = banners.find((b) => b.region === 'FR' && b.active);
  const usBanner = banners.find((b) => b.region === 'US' && b.active);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <div className="flex items-center gap-2">
            <Eye size={18} />
            <span>Aperçu mobile</span>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-fit max-w-none">
        <DialogHeader>
          <DialogTitle>Aperçu mobile</DialogTitle>
        </DialogHeader>
        <div className="flex flex-row gap-10 pt-2 pb-2">
          <PhonePreview banner={frBanner} label="🇫🇷 Mademoiselle Culotte" />
          <PhonePreview banner={usBanner} label="🇺🇸 Orpair" />
        </div>
      </DialogContent>
    </Dialog>
  );
};
